import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ComplianceFooter from '../components/ComplianceFooter';
import axios from 'axios';
import { LogOut, Plus, FileText, Clock, Award, XCircle, Bell, CreditCard, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import MyInvoices from '../components/MyInvoices';
import LifecycleProgressBadge from '../components/LifecycleProgressBadge';
import BuyerMembershipStatus from '../components/BuyerMembershipStatus';
import BuyerOpsWidgets from '../components/BuyerOpsWidgets';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tenders, setTenders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [membershipStatusData, setMembershipStatusData] = useState(null);
  const [stats, setStats] = useState({ total: 0, draft: 0, published: 0, awarded: 0, closed: 0 });
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchTenders();
    axios.get(`${API}/subscriptions/me`, { withCredentials: true }).then(r => setSubscription(r.data)).catch(() => {});
  }, []);

  const fetchTenders = async () => {
    try {
      const { data } = await axios.get(`${API}/tenders`, { withCredentials: true });
      setTenders(data);
      
      const POST_AWARD_STATUSES = ['awarded', 'po_issued', 'work_completed', 'noc_issued'];
      const stats = {
        total: data.length,
        draft: data.filter(t => t.status === 'draft').length,
        published: data.filter(t => t.status === 'published' || t.status === 'under_committee_review').length,
        awarded: data.filter(t => POST_AWARD_STATUSES.includes(t.status)).length,
        closed: data.filter(t => t.status === 'closed').length
      };
      setStats(stats);
    } catch (error) {
      toast.error('Failed to fetch tenders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    // Demo users return to the marketing home page; real users land on /login.
    const wasDemo = Boolean(user?.is_demo_user) || (typeof user?.email === 'string' && user.email.endsWith('.demo@procureoneultra.com'));
    const dest = wasDemo ? '/' : '/login';
    navigate(dest);
    await logout();
  };

  const getStatusBadge = (status) => {
    return <span className={`status-badge status-${status}`}>{status.replace(/_/g, ' ').toUpperCase()}</span>;
  };

  if (!user?.approved) {
    // Buyers with a manual payment awaiting verification (or rejected) can now access
    // a limited dashboard so they can track / resubmit their payment. Only truly
    // pending-admin-approval (legacy or otherwise) buyers are hard-blocked here.
    const showStatusOnly = ['pending_payment_verification', 'payment_rejected', 'clarification_requested'].includes(user?.membership_status);
    if (!showStatusOnly) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center max-w-md p-8">
            <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Account Pending Approval</h2>
            <p className="text-muted-foreground">Your buyer account is under review. You will be notified once approved by the admin.</p>
            <button onClick={handleLogout} className="btn-outline mt-6">Logout</button>
          </div>
        </div>
      );
    }
    // else fall through to the normal dashboard — the BuyerMembershipStatus banner will explain the state.
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-border/40 sticky top-0 z-50">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-lg sm:text-2xl font-bold tracking-tight" style={{ color: 'hsl(var(--primary))' }}>Buyer Dashboard</h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="hidden md:inline text-sm text-muted-foreground truncate max-w-[200px]">{user?.email}</span>
              <button onClick={() => navigate('/privacy')} className="text-xs text-navy underline hover:text-orange hidden sm:inline" data-testid="nav-privacy-btn">Privacy</button>
              <button onClick={handleLogout} className="px-2 sm:px-4 py-1.5 sm:py-2 rounded-md border border-input text-xs sm:text-sm" data-testid="logout-btn">
                <LogOut className="inline h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Manual-payment / activation status banner (Phase 1.1). Shown for all
            states except 'active' (in which case it renders nothing). */}
        <BuyerMembershipStatus onStatusChange={(d) => setMembershipStatusData(d)} />

        {/* Subscription banner — hidden when a manual-payment flow is already in progress
            (banner above already explains the state and lets the buyer resubmit if needed). */}
        {!subscription?.id && !['pending_payment_verification', 'payment_rejected', 'clarification_requested'].includes(membershipStatusData?.membership_status) && (
          <div className="mb-6 p-4 rounded-lg bg-gold/10 border-2 border-gold/40 flex flex-wrap items-center justify-between gap-3" data-testid="subscribe-banner-buyer">
            <div>
              <div className="font-bold text-navy">Activate your Buyer membership</div>
              <p className="text-xs text-muted-foreground">Post unlimited tenders, get vendor responses, and download invoices.</p>
            </div>
            <button onClick={() => navigate('/checkout?plan=buyer_yearly')} className="px-4 py-2 rounded bg-navy text-white text-sm font-semibold inline-flex items-center" data-testid="subscribe-cta-buyer">
              <CreditCard className="h-4 w-4 mr-2" /> Subscribe Now
            </button>
          </div>
        )}
        {subscription?.id && subscription.is_founding && (
          <div className="mb-6 p-3 rounded-lg bg-gold/15 border border-gold/40 flex items-center justify-between gap-3" data-testid="founding-banner-buyer">
            <div>
              <span className="text-sm font-bold text-navy">★ Founding Member</span>
              <span className="text-xs text-muted-foreground ml-2">Valid till {new Date(subscription.end_date).toLocaleDateString()}</span>
            </div>
            {user?.email?.toLowerCase().includes('demo') && (
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/demo/reset`, {}, { withCredentials: true });
                    toast.success('Demo restored to canonical state');
                    fetchTenders();
                  } catch (e) { toast.error('Reset failed'); }
                }}
                className="px-3 py-1.5 text-xs font-bold rounded border border-navy text-navy hover:bg-navy hover:text-white transition-colors"
                data-testid="reset-demo-btn"
              >
                Reset Demo
              </button>
            )}
          </div>
        )}

        {/* Operational Buyer Dashboard (Buyer V1 Phase A) */}
        <BuyerOpsWidgets />

        {/* Phase B quick actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <button onClick={() => navigate('/buyer-dashboard/tender/new')} className="p-4 rounded-lg border-2 border-orange/40 bg-orange/5 text-left hover:border-orange transition-colors" data-testid="action-new-tender">
            <Plus className="h-5 w-5 text-orange mb-1" />
            <div className="font-bold text-navy text-sm">New Tender</div>
            <div className="text-[11px] text-muted-foreground">Full procurement workflow</div>
          </button>
          <button onClick={() => navigate('/buyer-dashboard/quick-quotation')} className="p-4 rounded-lg border-2 border-navy/30 bg-card text-left hover:border-navy transition-colors" data-testid="action-quick-quotation">
            <FileText className="h-5 w-5 text-navy mb-1" />
            <div className="font-bold text-navy text-sm">Quick Quotation</div>
            <div className="text-[11px] text-muted-foreground">Society procurement or flat-owner</div>
          </button>
          <button onClick={() => navigate('/buyer-dashboard/templates')} className="p-4 rounded-lg border-2 border-navy/30 bg-card text-left hover:border-navy transition-colors" data-testid="action-templates">
            <FileText className="h-5 w-5 text-navy mb-1" />
            <div className="font-bold text-navy text-sm">Template Library</div>
            <div className="text-[11px] text-muted-foreground">Scope of Work · Approval letters</div>
          </button>
          <button onClick={() => navigate('/buyer-dashboard/settings/policy')} className="p-4 rounded-lg border-2 border-navy/30 bg-card text-left hover:border-navy transition-colors" data-testid="action-policy">
            <FileText className="h-5 w-5 text-navy mb-1" />
            <div className="font-bold text-navy text-sm">Procurement Policy</div>
            <div className="text-[11px] text-muted-foreground">Delegated authority register</div>
          </button>
          <button onClick={() => navigate('/buyer-dashboard/committee-management')} className="p-4 rounded-lg border-2 border-navy/30 bg-card text-left hover:border-navy transition-colors" data-testid="action-committee">
            <FileText className="h-5 w-5 text-navy mb-1" />
            <div className="font-bold text-navy text-sm">Committee Management</div>
            <div className="text-[11px] text-muted-foreground">Members · Observer · Resolution</div>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="p-5 rounded-lg border border-border bg-card" data-testid="stat-total-tenders">
            <FileText className="h-7 w-7 text-primary mb-2" />
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Tenders</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card" data-testid="stat-draft-tenders">
            <Clock className="h-7 w-7 text-yellow-500 mb-2" />
            <div className="text-2xl font-bold">{stats.draft}</div>
            <div className="text-sm text-muted-foreground">Drafts</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card" data-testid="stat-published-tenders">
            <FileText className="h-7 w-7 text-gold mb-2" />
            <div className="text-2xl font-bold">{stats.published}</div>
            <div className="text-sm text-muted-foreground">Published</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card" data-testid="stat-awarded-tenders">
            <Award className="h-7 w-7 text-green-500 mb-2" />
            <div className="text-2xl font-bold">{stats.awarded}</div>
            <div className="text-sm text-muted-foreground">Awarded</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card" data-testid="stat-closed-tenders">
            <CheckCircle2 className="h-7 w-7 text-gray-500 mb-2" />
            <div className="text-2xl font-bold">{stats.closed}</div>
            <div className="text-sm text-muted-foreground">Closed</div>
          </div>
        </div>

        {/* Actions */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/buyer-dashboard/tender/new')} 
            className="btn-primary"
            data-testid="create-tender-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Create New Tender
          </button>
        </div>

        {/* Tenders List */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Your Tenders</h2>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : tenders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-tenders-message">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tenders yet. Create your first tender to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Title</th>
                    <th className="text-left py-3 px-4 font-medium">Category</th>
                    <th className="text-left py-3 px-4 font-medium">Value</th>
                    <th className="text-left py-3 px-4 font-medium">Lifecycle</th>
                    <th className="text-left py-3 px-4 font-medium">End Date</th>
                    <th className="text-left py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenders.map((tender) => (
                    <tr key={tender.id} className="border-b border-border hover:bg-muted/50" data-testid={`tender-row-${tender.id}`}>
                      <td className="py-3 px-4">{tender.title}</td>
                      <td className="py-3 px-4">{tender.work_category}</td>
                      <td className="py-3 px-4">₹{tender.estimated_value.toLocaleString()}</td>
                      <td className="py-3 px-4"><LifecycleProgressBadge status={tender.status} /></td>
                      <td className="py-3 px-4">{new Date(tender.end_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <button 
                          onClick={() => navigate(`/tender/${tender.id}`)} 
                          className="text-primary hover:underline text-sm"
                          data-testid={`view-tender-${tender.id}`}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invoices & Receipts */}
        <div className="mt-6">
          <h2 className="text-xl font-semibold text-navy mb-3">My Invoices & Payment Receipts</h2>
          <MyInvoices />
        </div>
        <ComplianceFooter compact className="mt-8 border-t border-border pt-4" />
      </div>
    </div>
  );
};

export default BuyerDashboard;
